import { constants } from '@config';
import Table, {
  borderFlagsBottom,
  borderFlagsLeft,
  borderFlagsRight,
  borderFlagsTop
} from 'assets/js/editor/table/table';
import TableQuery from 'assets/js/editor/table/table-query';
import { calculateTextAreas } from 'assets/js/editor/ui/element-popupmenu/content/popupmenu-table-events';
import { capitalizeFirstLetter } from 'src/utils/string';

import ElementCreator from './abstract/element';
import TextSetter from './text';

const ElementSetter = require('./abstract/element');

class TableSetter extends ElementCreator {
  initialize () {
    super.initialize();

    this.handledAttributes = [
      ...this.handledAttributes,
      'text_content',
      'cell_foreground',
      'cell_background',
      'cell_lines',
      'original_font_size',
      'cell_font_weight',
      'cell_font_style',
      'cell_text_decoration',
      'cell_highlight',
      'cell_list',
      'cell_bullets',
      'cell_alignment',
      'cell_spacing',
      'anchor',
      'cell_case',
      'border_style',
      'stroke_dash',
      'stroke_fill',
      'stroke_width'
    ];
  }

  set (element, data) {
    this.table = new Table();
    this.table.registerTableSelection(element);
    ElementSetter.prototype.set.apply(this, arguments);
  }

  updateCellForeground (element, data) {
    const fill = data.cell_foreground.color;
    let cellRectElements = element.querySelectorAll('rect.table-cell-selected');

    if (!cellRectElements.length) {
      cellRectElements = element.querySelectorAll('rect:not(.highlight-rect)');
    }

    cellRectElements.forEach(cellRectElement => {
      const textAreaElement = cellRectElement.parentElement;
      const textArea = new INKIVE.Text.Textarea().setDOM(textAreaElement);

      if (textArea) {
        textArea.text.setValues({ fill });
        textArea.setValues({ smart_asset: data.smart_asset });
        textArea.text.styles = textArea.text.styles.filter(style => style.attribute !== 'fill');

        this.canvas.runExtensions('renderTextArea', { textArea });
      }
    });
  }

  updateCellBackground (element, data) {
    let cellRectElements = element.querySelectorAll('rect.table-cell-selected');

    if (!cellRectElements.length) {
      cellRectElements = element.querySelectorAll('rect:not(.highlight-rect)');
    }

    cellRectElements.forEach(cellRectElement => {
      cellRectElement.setAttribute('fill', data.cell_background.color);
      if (data.smart_asset) {
        cellRectElement.setAttribute(constants.smartAssets.attributes.definedAsset, data.smart_asset);
      }
    });

    this.trigger('background', element.getAttribute('id'), data.cell_background.color);
  }

  updateCellHighlight (element, data) {
    const highlight = data.cell_highlight.color;
    let cellRectElements = element.querySelectorAll('rect.table-cell-selected');

    if (!cellRectElements.length) {
      cellRectElements = element.querySelectorAll('rect:not(.highlight-rect)');
    }

    for (const cellRectElement of cellRectElements) {
      const textAreaElement = cellRectElement.parentElement;
      const textArea = new INKIVE.Text.Textarea().setDOM(textAreaElement);

      if (!textArea || !textArea.text.textContent) continue;

      textArea.text.setValues({ highlight });
      textArea.text.styles = textArea.text.styles.filter(style => style.attribute !== 'highlight');

      this.canvas.runExtensions('renderTextArea', { textArea });
    }

    this.trigger('highlight', element.getAttribute('id'), highlight);
  }

  updateCellLines (_, data) {
    INKIVE.Table.setBorderStyle({ where: data.cell_lines.where, stroke: data.cell_lines.color, strokeWidth: undefined, strokeDash: undefined });
  }

  updateBorderStyle (element, data) {
    const borderStyle = data.border_style;
    this.table.setBorderStyle({
      where: borderStyle.where,
      stroke: borderStyle.stroke,
      strokeWidth: borderStyle.strokeWidth,
      strokeDash: borderStyle.strokeDash,
      strokeDashOffset: borderStyle.strokeDashOffset
    });
  }

  updateCellFontWeight (element, data) {
    const fontWeight = data.cell_font_weight;

    let cellRectElements = element.querySelectorAll('rect.table-cell-selected');

    if (!cellRectElements.length) {
      cellRectElements = element.querySelectorAll('rect:not(.highlight-rect)');
    }

    for (const cellRectElement of cellRectElements) {
      const textAreaElement = cellRectElement.parentElement;
      const textArea = new INKIVE.Text.Textarea().setDOM(textAreaElement);

      if (!textArea) continue;

      textArea.text.setValues({ font_weight: fontWeight });
      textArea.text.styles = textArea.text.styles.filter(style => style.attribute !== 'font-weight');

      this.canvas.runExtensions('renderTextArea', { textArea });
    }

    this.trigger('font_weight', element.getAttribute('id'), fontWeight);
  }

  updateCellFontStyle (element, data) {
    const fontStyle = data.cell_font_style;

    let cellRectElements = element.querySelectorAll('rect.table-cell-selected');

    if (!cellRectElements.length) {
      cellRectElements = element.querySelectorAll('rect:not(.highlight-rect)');
    }

    for (const cellRectElement of cellRectElements) {
      const textAreaElement = cellRectElement.parentElement;
      const textArea = new INKIVE.Text.Textarea().setDOM(textAreaElement);

      if (!textArea) continue;

      textArea.text.setValues({ font_style: fontStyle });
      textArea.text.styles = textArea.text.styles.filter(style => style.attribute !== 'font-style');

      this.canvas.runExtensions('renderTextArea', { textArea });
    }

    this.trigger('font_style', element.getAttribute('id'), fontStyle);
  }

  updateCellTextDecoration (element, data) {
    const textDecoration = data.cell_text_decoration;
    let cellRectElements = element.querySelectorAll('rect.table-cell-selected');

    if (!cellRectElements.length) {
      cellRectElements = element.querySelectorAll('rect:not(.highlight-rect)');
    }

    cellRectElements.forEach(cellRectElement => {
      const textAreaElement = cellRectElement.parentElement;
      const textArea = new INKIVE.Text.Textarea().setDOM(textAreaElement);

      if (textArea && textArea.text.textContent) {
        textArea.text.setValues({ text_decoration: textDecoration });
        textAreaElement.querySelector('g.text').setAttribute('text-decoration', textDecoration);
      }
    });

    this.trigger('decoration', element.getAttribute('id'), textDecoration);
  }

  updateCellList (element, data) {
    const isOrderedList = data.cell_list;
    const orderedData = {
      ordered_list: isOrderedList ? 1 : 0,
      ordered_style: isOrderedList ? constants.text.orderedList.defaultStyleId : null,
      ordered_fill: null,
      ordered_mark: '.',
      ordered_start: null,
      ordered_width: null
    };

    let cellRectElements = element.querySelectorAll('rect.table-cell-selected');

    if (!cellRectElements.length) {
      cellRectElements = element.querySelectorAll('rect:not(.highlight-rect)');
    }

    for (const cellRectElement of cellRectElements) {
      this._updateTextOrderedList(cellRectElement.parentElement, orderedData);
    }

    this.trigger('ordered_list', element.getAttribute('id'), orderedData);
  }

  _updateTextOrderedList (element, data) {
    const textArea = new INKIVE.Text.Textarea().setDOM(element);

    if (!textArea || !textArea.text.textContent) return;
    let paragraphIndex = 0;
    textArea.text.setValues({
      orderedStart: data.ordered_start
    });

    textArea.text.strings.forEach(string => {
      const orderedLevelToSet = data.ordered_list === 0 ? data.ordered_list : Number(string.orderedLevel) || Number(string.bulletLevel) || data.ordered_list;
      if (orderedLevelToSet <= 1 && (string.bulletLevel || string.orderedLevel)) {
        Array.isArray(textArea.text.first_line_margin) && (textArea.text.first_line_margin[paragraphIndex] = 0);
        Array.isArray(textArea.text.line_margin) && (textArea.text.line_margin[paragraphIndex] = 0);
      }

      string.setValues({
        orderedLevel: orderedLevelToSet,
        orderedStyle: data.ordered_style,
        orderedWidth: data.ordered_width,
        orderedFill: data.ordered_fill
      });

      string.orderedLevel && string.bulletLevel && string.setValues({ bulletLevel: 0, bulletStyle: 0 });
      if (string.isEol) {
        paragraphIndex += 1;
      }
    });

    textArea.text.setValues({ ordered: !!data.ordered_list });
    this.canvas.runExtensions('renderTextArea', { textArea });
  }

  updateCellBullets (element, data) {
    const isBullets = data.cell_bullets;
    const bulletData = {
      bullet_points: isBullets ? 1 : 0,
      bullet_style: isBullets ? constants.text.bulletPoints.defaultStyleId : null,
      bullet_fill: null,
      bullet_width: null
    };

    let cellRectElements = element.querySelectorAll('rect.table-cell-selected');

    if (!cellRectElements.length) {
      cellRectElements = element.querySelectorAll('rect:not(.highlight-rect)');
    }

    for (const cellRectElement of cellRectElements) {
      this._updateTextBulletPoints(cellRectElement.parentElement, bulletData);
    }

    this.trigger('bullet_points', element.getAttribute('id'), isBullets);
  }

  _updateTextBulletPoints (element, data) {
    const textArea = new INKIVE.Text.Textarea().setDOM(element);

    if (!textArea || !textArea.text.textContent) return;

    let paragraphIndex = 0;
    if (data.bullet_points === true) {
      data.bullet_points = 1;
      data.bullet_style = constants.text.bulletPoints.defaultStyleId;
      data.bullet_width = 0;
      data.bullet_fill = null;
    } else if (data.bullet_points === false) {
      data.bullet_points = 0;
      data.bullet_style = 0;
      data.bullet_width = 0;
      data.bullet_fill = null;
    }

    textArea.text.strings.forEach(string => {
      const bulletLevelToSet = data.bullet_points === 0 ? data.bullet_points : Number(string.bulletLevel) || Number(string.orderedLevel) || data.bullet_points;
      if (bulletLevelToSet <= 1 && (string.bulletLevel || string.orderedLevel)) {
        Array.isArray(textArea.text.first_line_margin) && (textArea.text.first_line_margin[paragraphIndex] = 0);
        Array.isArray(textArea.text.line_margin) && (textArea.text.line_margin[paragraphIndex] = 0);
      }

      string.setValues({
        bulletLevel: bulletLevelToSet,
        bulletStyle: data.bullet_style,
        bulletWidth: data.bullet_width,
        bulletFill: data.bullet_fill
      });

      string.orderedLevel && string.bulletLevel && string.setValues({ orderedLevel: 0, orderedStyle: 0 });

      if (string.isEol) {
        paragraphIndex += 1;
      }
    });

    this.canvas.runExtensions('renderTextArea', { textArea });
  }

  updateCellAlignment (element, data) {
    const alignment = data.cell_alignment;
    this.table.setTextAlignment(alignment.horizontal, alignment.vertical);
  }

  updateCellSpacing (element, data) {
    const { value, action, type, paddings = [] } = data.cell_spacing;

    if (typeof value === 'undefined') return;

    const textAreas = data.cell_spacing?.textAreas?.length
      ? data.cell_spacing.textAreas
      : calculateTextAreas(element);

    for (let i = 0; i < textAreas.length; i++) {
      const textArea = textAreas[i];
      if (!textArea) continue;

      const spacing = (paddings[i] || 0) + parseFloat(value);
      textArea.text.setValues({ [type]: spacing });
      this.canvas.runExtensions('renderTextArea', { textArea });
    }

    action !== 'change' && this.trigger('spacing', element.getAttribute('id'), data.cell_spacing);
  }

  updateAnchor (element, data) {
    if (data.anchor === 'justify') data.anchor = 'justify_start';

    const textAreas = calculateTextAreas(element);

    for (const textArea of textAreas) {
      if (!textArea) continue;

      textArea.text.removeLineStyling();
      textArea.text.setValues({ text_anchor: data.anchor });
      if (textArea.text.letter_spacing.length) {
        textArea.text.setValues({ letter_spacing: 0 });
      }
      this.canvas.runExtensions('renderTextArea', { textArea });
    }

    this.trigger('anchor', element.getAttribute('id'), data.anchor);
  }

  updateCellCase (element, data) {
    const isUpperCase = !!data.cell_case;
    const textAreas = calculateTextAreas(element);

    for (const textArea of textAreas) {
      if (!textArea) continue;

      textArea.text.setTextCase(0, 0, isUpperCase);
      this.canvas.runExtensions('renderTextArea', { textArea });
    }

    this.trigger('case', element.getAttribute('id'), data.cell_case);
  }

  updateTextContent (_, data) {
    const textSetter = new TextSetter();
    textSetter.initialize();

    textSetter.set(data.textarea.dom, data);
  }

  updateStrokeDash (element, data) {
    const { position, strokeWidth, strokeDash, strokeDashOffset } = data.stroke_dash;

    const borderElements = this[`_get${capitalizeFirstLetter(position)}BorderELements`](element);
    for (const borderElement of borderElements) {
      if (this._isInnerMergedCellBorder(element, borderElement)) {
        continue;
      }

      borderElement.setAttribute('stroke-width', strokeWidth);
      strokeDash && borderElement.setAttribute('stroke-dasharray', `${strokeDash} ${strokeDash}`);
      strokeDashOffset && borderElement.setAttribute('stroke-dashoffset', strokeDashOffset);
      strokeDash || borderElement.removeAttribute('stroke-dasharray');
      strokeDashOffset || borderElement.removeAttribute('stroke-dashoffset');
    }
  }

  updateStrokeFill (element, data) {
    const { position, strokeFill } = data.stroke_fill;

    const borderElements = this[`_get${capitalizeFirstLetter(position)}BorderELements`](element);
    for (const borderElement of borderElements) {
      if (this._isInnerMergedCellBorder(element, borderElement)) {
        continue;
      }

      strokeFill && borderElement.setAttribute('stroke', strokeFill);
    }
  }

  updateStrokeWidth (element, data) {
    const { position, strokeWidth } = data.stroke_width;

    const borderElements = this[`_get${capitalizeFirstLetter(position)}BorderELements`](element);
    for (const borderElement of borderElements) {
      if (this._isInnerMergedCellBorder(element, borderElement)) {
        continue;
      }

      borderElement.setAttribute('stroke-width', strokeWidth);
    }
  }

  /**
   *
   * @param {HTMLElement} tableElement
   * @param {HTMLElement} borderElement
   * @returns {boolean}
   * @private
   */
  _isInnerMergedCellBorder (tableElement, borderElement) {
    const borderData = TableQuery.getBorderData(borderElement, true);
    const isVertical = borderElement.classList.contains('table-border-v');
    if (isVertical) {
      const rightCellIndex = borderData.columnIndex;
      const leftCellIndex = rightCellIndex - 1;
      const rowIndex = borderData.rowIndex;

      const leftCell = TableQuery.getCellElementAt(tableElement, rowIndex, leftCellIndex);
      const rightCell = TableQuery.getCellElementAt(tableElement, rowIndex, rightCellIndex);

      if (!leftCell || !rightCell) {
        return false;
      }

      if (!TableQuery.isMerged(leftCell) || !TableQuery.isMerged(rightCell)) {
        return false;
      }

      return TableQuery.getMergeId(leftCell) === TableQuery.getMergeId(rightCell);
    } else {
      const topCellIndex = borderData.rowIndex - 1;
      const bottomCellIndex = topCellIndex + 1;
      const columnIndex = borderData.columnIndex;

      const topCell = TableQuery.getCellElementAt(tableElement, topCellIndex, columnIndex);
      const bottomCell = TableQuery.getCellElementAt(tableElement, bottomCellIndex, columnIndex);

      if (!topCell || !bottomCell) {
        return false;
      }

      if (!TableQuery.isMerged(topCell) || !TableQuery.isMerged(bottomCell)) {
        return false;
      }

      return TableQuery.getMergeId(topCell) === TableQuery.getMergeId(bottomCell);
    }
  }

  _getAllBorderELements (tableElement) {
    const selectedRectElements = tableElement.querySelectorAll('rect.table-cell-selected');

    if (!selectedRectElements?.length) return [...tableElement.querySelectorAll('line.table-border')];

    const borderElements = [];
    const selectedCellElements = this._getSelectedCellElements(tableElement, selectedRectElements);
    for (const selectedCell of selectedCellElements) {
      const borderTop = TableQuery.getBorderElementByCell(selectedCell, borderFlagsTop);
      const borderBottom = TableQuery.getBorderElementByCell(selectedCell, borderFlagsBottom);
      const borderLeft = TableQuery.getBorderElementByCell(selectedCell, borderFlagsLeft);
      const borderRight = TableQuery.getBorderElementByCell(selectedCell, borderFlagsRight);

      borderElements.push(borderTop, borderBottom, borderLeft, borderRight);
    }

    return [...new Set(borderElements)];
  }

  _getTopBorderELements (tableElement) {
    const selectedRectElements = tableElement.querySelectorAll('rect.table-cell-selected');

    if (!selectedRectElements?.length) {
      const firstRowElement = tableElement.querySelector('g.table-row');
      return [...TableQuery.getRowTopBorderElements(firstRowElement)];
    }

    const borderElements = [];
    const selectedCellElements = this._getSelectedCellElements(tableElement, selectedRectElements);
    for (const selectedCell of selectedCellElements) {
      const { rowIndex, columnIndex } = TableQuery.getCellIndexData(selectedCell);

      const topNeighborCell = TableQuery.getCellElementAt(tableElement, rowIndex - 1, columnIndex);
      if (topNeighborCell?.querySelector('.table-cell-selected')) continue;

      const borderTop = TableQuery.getBorderElementByCell(selectedCell, borderFlagsTop);
      borderElements.push(borderTop);
    }

    return [...new Set(borderElements)];
  }

  _getBottomBorderELements (tableElement) {
    const selectedRectElements = tableElement.querySelectorAll('rect.table-cell-selected');

    if (!selectedRectElements?.length) {
      const rowElements = tableElement.querySelectorAll('g.table-row');
      const lastRowElement = rowElements[rowElements.length - 1];
      return [...TableQuery.getRowBottomBorderElements(lastRowElement)];
    }

    const borderElements = [];
    const selectedCellElements = this._getSelectedCellElements(tableElement, selectedRectElements);
    for (const selectedCell of selectedCellElements) {
      const { rowIndex, columnIndex } = TableQuery.getCellIndexData(selectedCell);

      const bottomNeighborCell = TableQuery.getCellElementAt(tableElement, rowIndex + 1, columnIndex);
      if (bottomNeighborCell?.querySelector('.table-cell-selected')) continue;

      const borderBottom = TableQuery.getBorderElementByCell(selectedCell, borderFlagsBottom);
      borderElements.push(borderBottom);
    }

    return [...new Set(borderElements)];
  }

  _getLeftBorderELements (tableElement) {
    const selectedRectElements = tableElement.querySelectorAll('rect.table-cell-selected');

    if (!selectedRectElements?.length) {
      return [...tableElement.querySelectorAll('g.table-borders line.table-border-v.index-c-0')];
    }

    const borderElements = [];
    const selectedCellElements = this._getSelectedCellElements(tableElement, selectedRectElements);
    for (const selectedCell of selectedCellElements) {
      const { rowIndex, columnIndex } = TableQuery.getCellIndexData(selectedCell);

      const leftNeighborCell = TableQuery.getCellElementAt(tableElement, rowIndex, columnIndex - 1);
      if (leftNeighborCell?.querySelector('.table-cell-selected')) continue;

      const borderToUpdate = TableQuery.getBorderElementByCell(selectedCell, borderFlagsLeft);
      borderElements.push(borderToUpdate);
    }

    return [...new Set(borderElements)];
  }

  _getRightBorderELements (tableElement) {
    const selectedRectElements = tableElement.querySelectorAll('rect.table-cell-selected');

    if (!selectedRectElements?.length) {
      const rowElement = tableElement.querySelector('g.table-row');
      const lastCellElement = rowElement.lastChild;
      const { columnIndex } = TableQuery.getCellIndexData(lastCellElement);

      return [...tableElement.querySelectorAll(`g.table-borders line.table-border-v.index-c-${columnIndex + 1}`)];
    }

    const borderElements = [];
    const selectedCellElements = this._getSelectedCellElements(tableElement, selectedRectElements);
    for (const selectedCell of selectedCellElements) {
      const { rowIndex, columnIndex } = TableQuery.getCellIndexData(selectedCell);

      const rightNeighborCell = TableQuery.getCellElementAt(tableElement, rowIndex, columnIndex + 1);
      if (rightNeighborCell?.querySelector('.table-cell-selected')) continue;

      const borderToUpdate = TableQuery.getBorderElementByCell(selectedCell, borderFlagsRight);
      borderElements.push(borderToUpdate);
    }

    return [...new Set(borderElements)];
  }

  _getOuterBorderELements (tableElement) {
    const selectedRectElements = tableElement.querySelectorAll('rect.table-cell-selected');

    if (!selectedRectElements?.length) {
      const rowElements = tableElement.querySelectorAll('g.table-row');
      const lastRowIndex = rowElements.length - 1;

      const lastRowCellElements = TableQuery.getAllCellElementsAtRow(tableElement, lastRowIndex);
      const lastColumnIndex = lastRowCellElements.length - 1;

      return [
        ...tableElement.querySelectorAll(`g.table-borders line.table-border-v.index-c-${0}`),
        ...tableElement.querySelectorAll(`g.table-borders line.table-border-v.index-c-${lastColumnIndex + 1}`),
        ...tableElement.querySelectorAll(`g.table-borders line.table-border-h.index-r-${0}`),
        ...tableElement.querySelectorAll(`g.table-borders line.table-border-h.index-r-${lastRowIndex + 1}`)
      ];
    }

    const borderElements = [];
    const selectedCellElements = this._getSelectedCellElements(tableElement, selectedRectElements);
    for (const selectedCell of selectedCellElements) {
      const unconnectedSides = this._getUnconnectedSides(tableElement, selectedCell);
      borderElements.push(...unconnectedSides);
    }

    return [...new Set(borderElements)];
  }

  _getInnerBorderELements (tableElement) {
    const outerBorderElements = this._getOuterBorderELements(tableElement);
    const allBorders = this._getAllBorderELements(tableElement);

    const innerBorderElements = allBorders.filter(border => !outerBorderElements.includes(border));
    return innerBorderElements;
  }

  _getHorizontalBorderELements (tableElement) {
    const innerBorderElements = this._getInnerBorderELements(tableElement);
    const allHorizontalBorders = this._getAllBorderELements(tableElement).filter(border => border.classList.contains('table-border-h'));
    const innerHorizontalBorders = allHorizontalBorders.filter(border => innerBorderElements.includes(border));

    return innerHorizontalBorders;
  }

  _getVerticalBorderELements (tableElement) {
    const innerBorderElements = this._getInnerBorderELements(tableElement);
    const allVerticalBorders = this._getAllBorderELements(tableElement).filter(border => border.classList.contains('table-border-v'));
    const innerVerticalBorders = allVerticalBorders.filter(border => innerBorderElements.includes(border));

    return innerVerticalBorders;
  }

  _getUnconnectedSides (tableElement, cellElement) {
    const unconnectedSides = [];

    const { rowIndex, columnIndex } = TableQuery.getCellIndexData(cellElement);

    const rightNeighborCell = this._getMasterCellElementAt(tableElement, rowIndex, columnIndex + 1)?.querySelector('.table-cell-selected');
    const leftNeighborCell = this._getMasterCellElementAt(tableElement, rowIndex, columnIndex - 1)?.querySelector('.table-cell-selected');
    const topNeighborCell = this._getMasterCellElementAt(tableElement, rowIndex - 1, columnIndex)?.querySelector('.table-cell-selected');
    const bottomNeighborCell = this._getMasterCellElementAt(tableElement, rowIndex + 1, columnIndex)?.querySelector('.table-cell-selected');

    rightNeighborCell || unconnectedSides.push(TableQuery.getBorderElementByCell(cellElement, borderFlagsRight));
    leftNeighborCell || unconnectedSides.push(TableQuery.getBorderElementByCell(cellElement, borderFlagsLeft));
    topNeighborCell || unconnectedSides.push(TableQuery.getBorderElementByCell(cellElement, borderFlagsTop));
    bottomNeighborCell || unconnectedSides.push(TableQuery.getBorderElementByCell(cellElement, borderFlagsBottom));

    return unconnectedSides;
  }

  _getMasterCellElementAt (tableElement, rowIndex, columnIndex) {
    const cellElement = TableQuery.getCellElementAt(tableElement, rowIndex, columnIndex);
    if (!cellElement) return null;

    if (!TableQuery.isMergeSlave(cellElement)) return cellElement;
    const mergeId = TableQuery.getMergeId(cellElement);
    return tableElement.querySelector(`g.table-cell-max.m-${mergeId}`);
  }

  _getSelectedCellElements (tableElement, selectedRectElements) {
    const selectedCellElements = [];
    for (const selectedRect of selectedRectElements) {
      const selectedCell = selectedRect.closest('g.table-cell');
      selectedCellElements.push(selectedCell);

      if (!TableQuery.isMergeMaster(selectedCell)) continue;

      const mergeId = TableQuery.getMergeId(selectedCell);
      selectedCellElements.push(...tableElement.querySelectorAll(`g.table-cell-min.m-${mergeId}`));
    }

    return selectedCellElements;
  }
}

module.exports = TableSetter;
